import { Component, effect, Input, OnInit, signal, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Item } from "../../../pojo/Item";
import { UserprefService } from "../../../services/userpref.service";
import { SeSearchService } from "./se.search.service";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25RadioModule } from "../../s25-radio/s25.radio.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25QLModule } from "../../s25-ql/s25ql.module";
import { TileItem } from "./item.tile.component";
import { Proto } from "../../../pojo/Proto";

export type SESearchType = "organizations" | "events";
export interface SEOrgSearchConfig {
    compactResultLimit?: number;
    onViewAllResults?: () => void;
    defaultSearchType?: SESearchType; // Which type to show first (defaults to 'events')
}

@TypeManagerDecorator("s25-ng-se-search")
@Component({
    selector: "s25-ng-se-search",
    template: `
        @if (isInit()) {
            <div class="se-org-search">
                <div class="search-type-toggle">
                    <button
                        class="type-tab"
                        [class.active]="searchType() === 'events'"
                        (click)="setSearchType('events')"
                    >
                        <s25-ng-icon [type]="'event'" [size]="16"></s25-ng-icon>
                        Events
                    </button>
                    <button
                        class="type-tab"
                        [class.active]="searchType() === 'organizations'"
                        (click)="setSearchType('organizations')"
                    >
                        <s25-ng-icon [type]="'organization'" [size]="16"></s25-ng-icon>
                        Organizations
                    </button>
                </div>

                <!-- Error Message -->
                @if (errorMessage()) {
                    <div class="error-message">
                        <pre class="ngBold">Error: {{ errorMessage() }}</pre>
                    </div>
                }

                <div class="search-wrapper">
                    <s25-ng-icon [type]="'search'" [size]="20" class="search-icon"></s25-ng-icon>
                    <input
                        class="search-input"
                        placeholder="Enter your search term"
                        [ngModel]="searchInput()"
                        (ngModelChange)="searchInput.set($event)"
                        (keyup.enter)="onSearch()"
                    />
                    <button class="search-button" (click)="onSearch()">Search</button>
                </div>
            </div>
        }
    `,
    styles: `
        .se-org-search {
            width: 100%;
        }

        .search-header {
            text-align: center;
            margin-bottom: 1.5rem;
        }

        .search-header h2 {
            font-size: 24px;
            margin: 0 0 0.5rem 0;
            color: #fff;
            font-weight: 600;
            border: none;
            padding: 0;
        }

        .search-subtitle {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.9);
            margin: 0;
        }

        .search-type-toggle {
            display: flex;
            gap: 12px;
            justify-content: center;
            margin-bottom: 1.5rem;
        }

        .type-tab {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background-color: rgba(255, 255, 255, 0.15);
            color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.25);
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            backdrop-filter: blur(10px);
        }

        .type-tab:hover {
            background-color: rgba(255, 255, 255, 0.25);
            color: #fff;
            border-color: rgba(255, 255, 255, 0.4);
            transform: translateY(-1px);
        }

        .type-tab.active {
            background-color: #fff;
            color: #0066cc;
            border-color: #fff;
        }

        .type-tab.active:hover {
            background-color: #f8f9fa;
            transform: translateY(-1px);
        }

        .type-tab:active {
            transform: translateY(0);
        }

        ::ng-deep .type-tab svg {
            vertical-align: middle;
        }

        .error-message {
            background-color: rgba(220, 38, 38, 0.1);
            border: 1px solid rgba(220, 38, 38, 0.3);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 1rem;
            color: #fff;
        }

        .error-message pre {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
        }

        .search-wrapper {
            position: relative;
            width: 100%;
            max-width: 600px;
            display: flex;
            align-items: center;
            background: white;
            border-radius: 50px;
            overflow: hidden;
            margin: 0 auto;
        }

        .search-icon {
            position: absolute;
            left: 1.5rem;
            color: #718096;
            pointer-events: none;
        }

        .search-input {
            flex: 1;
            padding: 1rem 1.5rem 1rem 3.5rem;
            border: none;
            outline: none;
            font-size: 1rem;
            color: #2d3748;
        }

        .search-input::placeholder {
            color: #a0aec0;
        }

        .search-button {
            padding: 1rem 2rem;
            background: #0066cc;
            color: white;
            border: none;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s ease;
        }

        .search-button:hover {
            background: #0052a3;
        }
    `,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        S25QLModule,
        S25ToggleButtonModule,
        S25ModalModule,
        S25RadioModule,
        S25IconComponent,
        S25MultiselectModule,
        S25DatepickerModule,
    ],
})
export class SeSearchComponent implements OnInit {
    // Configuration object - control all search features
    @Input() config: SEOrgSearchConfig = {};

    // Computed config properties with defaults
    get compactResultLimit(): number {
        return this.config.compactResultLimit ?? 6;
    }
    get onViewAllResults(): (() => void) | undefined {
        return this.config.onViewAllResults;
    }

    // LocalStorage key for persisting search type
    private readonly SEARCH_TYPE_STORAGE_KEY = "se-search-type";

    // Component state
    isInit = signal(false);
    loggedIn = signal(false);

    // Search type state with localStorage persistence
    searchType = signal<SESearchType>(this.getInitialSearchType());

    // Get current item type ID based on search type
    currentItemTypeId = signal<Item.Ids>(Item.Ids.Event);

    // Search state
    searchInput = signal("");
    // Results state
    results = signal<TileItem[]>([]);
    resultsCount = signal<number>(0);
    hasSearched = signal<boolean>(false);
    isSearching = signal<boolean>(false);
    errorMessage = signal<string | null>(null);
    isExporting = signal<boolean>(false);

    // Pagination state
    currentPage = signal<number>(1);
    pageSize = signal<number>(20);
    paginateKey = signal<string>("");

    // Save search state
    saveSearchName = "";
    saveSearchFavorite = false;
    saveSearchError = "";
    isSavingSearch = false;

    constructor() {
        // Effect: Update item type ID when search type changes
        effect(() => {
            const type = this.searchType();
            this.currentItemTypeId.set(type === "events" ? Item.Ids.Event : Item.Ids.Organization);
        });
    }

    async ngOnInit(): Promise<void> {
        this.loggedIn.set(await UserprefService.getLoggedIn());
        this.isInit.set(true);
    }

    /**
     * Get initial search type from localStorage or config
     */
    private getInitialSearchType(): SESearchType {
        // Check localStorage first
        const stored = localStorage.getItem(this.SEARCH_TYPE_STORAGE_KEY) as SESearchType;
        if (stored && (stored === "organizations" || stored === "events")) {
            return stored;
        }
        // Fall back to config or default to 'events'
        return this.config.defaultSearchType || "events";
    }

    /**
     * Set search type and persist to localStorage
     */
    setSearchType(type: SESearchType): void {
        this.searchType.set(type);
        localStorage.setItem(this.SEARCH_TYPE_STORAGE_KEY, type);

        // Reset search when switching types
        this.onReset();
    }

    /**
     * Handle text input changes
     */
    onTextInputChange(): void {
        this.errorMessage.set(null);
    }

    /**
     * Handle error messages from search input
     */
    onErrorMessage(message: string | null): void {
        this.errorMessage.set(message);
    }

    /**
     * Execute the search
     */
    async onSearch(): Promise<void> {
        try {
            this.errorMessage.set(null);
            this.hasSearched.set(true);
            this.isSearching.set(true);

            // Reset to page 1 when starting a new search
            this.currentPage.set(1);
            this.paginateKey.set("T");

            await this.runSearch();
        } catch (error) {
            console.error("Search error:", error);
            this.errorMessage.set(error.message || "An error occurred during search");
        } finally {
            this.isSearching.set(false);
        }
    }

    setPaginate(response: Proto.Paginate): void {
        this.paginateKey.set(response.paginate_key);

        this.pageSize.set(response.page_size);
        this.resultsCount.set(Number(response.total_results));
    }

    async runSearch(): Promise<void> {
        switch (this.searchType()) {
            case "events":
                await this.runEventSearch();
                break;
            case "organizations":
                await this.runOrganizationSearch();
                break;
        }
    }

    async runEventSearch(): Promise<void> {
        try {
            const response = await SeSearchService.searchEvents(
                this.searchInput(),
                this.currentPage(),
                this.pageSize(),
            );

            // Map event documents to tile items with UpcomingEvent-compatible format
            const tileItems: TileItem[] = response.events.map((event) => ({
                ...event,
                itemTypeId: Item.Ids.Event,
                // Map startDate to date for compatibility with event tile component
                date: event.startDate || "",
                description: event.txt || "",
            }));

            this.results.set(tileItems);
            this.setPaginate(response);
        } catch (error) {
            console.error("Error searching events:", error);
            throw error;
        }
    }

    async runOrganizationSearch(): Promise<void> {
        try {
            const response = await SeSearchService.searchOrganizations(
                this.searchInput(),
                this.currentPage(),
                this.pageSize(),
            );

            // The documents already have all fields needed, just ensure itemTypeId is set
            const tileItems: TileItem[] = response.organizations.map((org) => ({
                ...org,
                itemTypeId: Item.Ids.Organization,
            }));

            this.results.set(tileItems);
            this.setPaginate(response);
        } catch (error) {
            console.error("Error searching organizations:", error);
            throw error;
        }
    }

    /**
     * Handle page change from pagination component
     */
    async onPageChange(newPage: number): Promise<void> {
        this.currentPage.set(newPage);

        // Re-run the search with the new page
        try {
            this.isSearching.set(true);
            await this.runSearch();
        } catch (error) {
            console.error("Error changing page:", error);
            this.errorMessage.set(error.message || "An error occurred while changing pages");
        } finally {
            this.isSearching.set(false);
        }
    }

    /**
     * Handle "Load More" button click
     * Loads next page and appends to existing results
     * Preserves scroll position to avoid jumping to top
     */
    async onLoadMore(): Promise<void> {
        // Store current scroll position before loading
        const scrollPosition = window.scrollY || window.pageYOffset;

        try {
            this.isSearching.set(true);

            // Increment page
            const nextPage = this.currentPage() + 1;
            this.currentPage.set(nextPage);

            // Fetch next page based on search type
            let newItems: TileItem[] = [];

            if (this.searchType() === "organizations") {
                const response = await SeSearchService.searchOrganizations(
                    this.searchInput(),
                    nextPage,
                    this.pageSize(),
                    this.paginateKey(),
                );
                newItems = response.organizations.map((org) => ({
                    ...org,
                    itemTypeId: Item.Ids.Organization,
                }));
            } else if (this.searchType() === "events") {
                const response = await SeSearchService.searchEvents(
                    this.searchInput(),
                    nextPage,
                    this.pageSize(),
                    this.paginateKey(),
                );
                newItems = response.events.map((event) => ({
                    ...event,
                    itemTypeId: Item.Ids.Event,
                    date: event.startDate || "",
                    description: event.txt || "",
                }));
            }

            // Append new results to existing ones
            this.results.set([...this.results(), ...newItems]);
            // Total count stays the same

            // Restore scroll position after a brief delay to allow DOM update
            setTimeout(() => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: "instant", // Use 'instant' to avoid animation, or 'smooth' for smooth scroll
                });
            }, 0);
        } catch (error) {
            console.error("Error loading more:", error);
            this.errorMessage.set(error.message || "An error occurred while loading more results");

            // Restore scroll position even on error
            setTimeout(() => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: "instant",
                });
            }, 0);
        } finally {
            this.isSearching.set(false);
        }
    }

    /**
     * Reset search to initial state
     */
    onReset(): void {
        this.searchInput.set("");
        this.results.set([]);
        this.hasSearched.set(false);
        this.errorMessage.set(null);
        this.currentPage.set(1);
    }

    /**
     * Handle "View All" button click in compact mode
     */
    onViewAll(): void {
        if (this.config.onViewAllResults) {
            this.config.onViewAllResults();
        }
    }
}
